import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { formatCurrency } from '../utils/dateUtils.ts';
import { getDashboard, getPortfolio, getIncome, FIIDashboardResponse, IndexEntry, FIIAssetPortfolioItem, FIIIncome } from '../services/fiiApi.ts';
import './FIIDashboard.css';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

function getLast12MonthsDateRange(): { start_date: string; end_date: string } {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
  return {
    start_date: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-01`,
    end_date: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`,
  };
}

function aggregateIncomeByMonth(incomes: FIIIncome[]): { month: string; total: number }[] {
  const byMonth: Record<string, number> = {};
  for (const inc of incomes) {
    const d = inc.date.slice(0, 7); // YYYY-MM
    byMonth[d] = (byMonth[d] ?? 0) + inc.total_value;
  }
  const now = new Date();
  const result: { month: string; total: number }[] = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    result.push({
      month: key,
      total: byMonth[key] ?? 0,
    });
  }
  return result;
}

// Paleta de cores para segmentos (consistente entre os dois gráficos)
const SEGMENT_COLORS = [
  '#004AAD', '#00B894', '#667eea', '#764ba2', '#f59e0b',
  '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6',
  '#6366f1', '#f97316',
];

function useSegmentDistributions(assets: FIIAssetPortfolioItem[] | null) {
  return useMemo(() => {
    if (!assets || assets.length === 0) {
      return { current: [], ideal: [], segmentColors: {} as Record<string, string> };
    }
    const totalValue = assets.reduce((s, a) => s + a.current_value, 0);

    const currentBySegment: Record<string, number> = {};
    const idealBySegment: Record<string, number> = {};

    for (const a of assets) {
      const seg = a.segment || 'Outros';
      currentBySegment[seg] = (currentBySegment[seg] ?? 0) + a.current_value;
      idealBySegment[seg] = (idealBySegment[seg] ?? 0) + (a.target_allocation_pct ?? 0);
    }

    const allSegments = [...new Set([...Object.keys(currentBySegment), ...Object.keys(idealBySegment)])].sort();
    const segmentColors: Record<string, string> = {};
    allSegments.forEach((seg, i) => {
      segmentColors[seg] = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
    });

    const current = allSegments
      .map(seg => ({
        segment: seg,
        value: totalValue > 0 ? (currentBySegment[seg] ?? 0) / totalValue * 100 : 0,
      }))
      .filter(x => x.value > 0.01);

    const idealTotal = allSegments.reduce((s, seg) => s + (idealBySegment[seg] ?? 0), 0);
    const ideal = allSegments
      .map(seg => ({
        segment: seg,
        value: idealTotal > 0 ? ((idealBySegment[seg] ?? 0) / idealTotal) * 100 : 0,
      }))
      .filter(x => x.value > 0.01);

    return { current, ideal, segmentColors };
  }, [assets]);
}

const FIIDashboard: React.FC = () => {
  const [data, setData] = useState<FIIDashboardResponse | null>(null);
  const [portfolio, setPortfolio] = useState<{ assets: FIIAssetPortfolioItem[] } | null>(null);
  const [incomes, setIncomes] = useState<FIIIncome[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { start_date, end_date } = getLast12MonthsDateRange();
      const [d, p, inc] = await Promise.all([
        getDashboard(),
        getPortfolio(),
        getIncome({ start_date, end_date }),
      ]);
      setData(d);
      setPortfolio(p);
      setIncomes(Array.isArray(inc) ? inc : []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const { current: currentDist, ideal: idealDist, segmentColors } = useSegmentDistributions(portfolio?.assets ?? null);
  const monthlyIncome = useMemo(() => aggregateIncomeByMonth(incomes), [incomes]);

  useEffect(() => { load(); }, [load]);

  const formatPct = (v: number | null | undefined) => {
    if (v == null) return 'N/A';
    return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
  };

  const pctClass = (v: number | null | undefined) => {
    if (v == null) return '';
    return v >= 0 ? 'positive' : 'negative';
  };

  const renderIndex = (name: string, idx: IndexEntry | null, isRate: boolean) => (
    <div className="index-card">
      <div className="index-name">{name}</div>
      <div className="index-value">
        {idx ? (isRate ? `${idx.value.toFixed(2)}%` : idx.value.toFixed(2)) : 'N/D'}
      </div>
      {idx && (
        <div className={`index-meta ${idx.stale ? 'stale' : ''}`}>
          {idx.stale ? '⚠️ Desatualizado' : `Atualizado: ${idx.period}`}
        </div>
      )}
    </div>
  );

  return (
    <div className="fii-dashboard">
      <div className="page-header">
        <div>
          <h1>📈 Dashboard FII</h1>
          <p>Visão geral da sua carteira e índices de referência</p>
        </div>
        <button className="secondary-button" onClick={load}>🔄 Atualizar</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading">Carregando...</div>
      ) : data ? (
        <>
          {/* Índices de Referência - no início */}
          <section className="section">
            <h2>Índices de Referência</h2>
            <div className="indices-grid">
              {renderIndex('Selic (a.a.)', data.indices.selic, true)}
              {renderIndex('CDI (diário)', data.indices.cdi, true)}
              {renderIndex('IPCA (acum. 12m)', data.indices.ipca, true)}
              {renderIndex('IFIX', data.indices.ifix, false)}
            </div>
            <p className="indices-note">
              CDI: taxa diária. IPCA: acumulado últimos 12 meses. Selic: meta anual. IFIX: pontos atuais.
              Fonte: BCB SGS (Selic, CDI, IPCA) e HG Finance (IFIX). Cache: 24h.
            </p>
          </section>

          {/* Portfolio Summary */}
          <section className="section">
            <h2>Resumo da Carteira</h2>
            <div className="kpi-grid">
              <div className="kpi-card">
                <div className="kpi-label">Total Investido</div>
                <div className="kpi-value">{formatCurrency(data.portfolio.total_invested)}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Valor Atual</div>
                <div className="kpi-value">{formatCurrency(data.portfolio.current_value)}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Lucro/Perda</div>
                <div className={`kpi-value ${pctClass(data.portfolio.profitability_rs)}`}>
                  {formatCurrency(data.portfolio.profitability_rs)}
                </div>
                <div className={`kpi-sub ${pctClass(data.portfolio.profitability_pct)}`}>
                  {formatPct(data.portfolio.profitability_pct)}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Rendimentos Totais</div>
                <div className="kpi-value positive">{formatCurrency(data.portfolio.total_income)}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Dividend Yield Total</div>
                <div className={`kpi-value ${pctClass(data.portfolio.income_yield_pct)}`}>
                  {formatPct(data.portfolio.income_yield_pct)}
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Retorno Total</div>
                <div className={`kpi-value ${pctClass(data.portfolio.total_return_pct)}`}>
                  {formatPct(data.portfolio.total_return_pct)}
                </div>
              </div>
            </div>
            {data.portfolio.quotes_stale && (
              <div className="stale-banner">
                ⚠️ Cotações desatualizadas — última atualização: {data.portfolio.quotes_updated_at || 'N/D'}
              </div>
            )}
          </section>

          {/* Distribuição por Segmento */}
          <section className="section">
            <h2>Distribuição por Segmento</h2>
            <div className="segment-charts">
              <div className="segment-chart-card">
                <h3>Distribuição Atual</h3>
                {currentDist.length === 0 ? (
                  <div className="chart-empty">Nenhum ativo na carteira</div>
                ) : (
                  <div className="chart-wrapper">
                    <Pie
                      data={{
                        labels: currentDist.map(d => d.segment),
                        datasets: [{
                          data: currentDist.map(d => d.value),
                          backgroundColor: currentDist.map(d => segmentColors[d.segment] ?? '#999'),
                          borderWidth: 2,
                          borderColor: '#fff',
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'bottom' },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => {
                                const pct = ctx.parsed ?? 0;
                                return `${ctx.label}: ${pct.toFixed(1)}%`;
                              },
                            },
                          },
                          datalabels: {
                            display: true,
                            color: '#fff',
                            font: { weight: 'bold' as const, size: 11 },
                            formatter: (v: number) => (v >= 5 ? `${v.toFixed(0)}%` : ''),
                          },
                        },
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="segment-chart-card">
                <h3>Distribuição Ideal</h3>
                {idealDist.length === 0 ? (
                  <div className="chart-empty">Nenhuma alocação alvo definida</div>
                ) : (
                  <div className="chart-wrapper">
                    <Pie
                      data={{
                        labels: idealDist.map(d => d.segment),
                        datasets: [{
                          data: idealDist.map(d => d.value),
                          backgroundColor: idealDist.map(d => segmentColors[d.segment] ?? '#999'),
                          borderWidth: 2,
                          borderColor: '#fff',
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'bottom' },
                          tooltip: {
                            callbacks: {
                              label: (ctx) => {
                                const pct = ctx.parsed ?? 0;
                                return `${ctx.label}: ${pct.toFixed(1)}%`;
                              },
                            },
                          },
                          datalabels: {
                            display: true,
                            color: '#fff',
                            font: { weight: 'bold' as const, size: 11 },
                            formatter: (v: number) => (v >= 5 ? `${v.toFixed(0)}%` : ''),
                          },
                        },
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Proventos mensais */}
          <section className="section">
            <h2>Proventos nos Últimos 12 Meses</h2>
            <div className="income-chart-card">
              <div className="income-chart-wrapper">
                <Bar
                  data={{
                    labels: monthlyIncome.map(d => {
                      const [y, m] = d.month.split('-');
                      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
                    }),
                    datasets: [{
                      label: 'Proventos (R$)',
                      data: monthlyIncome.map(d => d.total),
                      backgroundColor: '#00B894',
                      borderRadius: 6,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            const v = ctx.parsed?.y ?? 0;
                            return `${formatCurrency(v)}`;
                          },
                        },
                      },
                      datalabels: {
                        display: (ctx) => (ctx.dataset.data[ctx.dataIndex] as number) > 0,
                        anchor: 'end' as const,
                        align: 'top' as const,
                        formatter: (v: number) => formatCurrency(v),
                        font: { size: 10 },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: (v) => formatCurrency(Number(v)),
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
};

export default FIIDashboard;
